import { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom";

import NavBar from "./Components/NavBar";
import Footer from "./Components/Footer";
import Home from "./Pages/Home";
import About from "./Pages/AboutUs";
import Products from "./Pages/Products";
import ProductCatalogue from "./Pages/ProductCatalogue";
import Certifications from "./Pages/Certifications";
import Infrastructure from "./Pages/Infrastructure";
import Contact from "./Pages/ContactUs";
import ScrollToTop from "./Components/ScrollToTop";
import ReviewPrompt from "./Components/ReviewPrompt";
import AnalyticsTracker from "./Components/AnalyticsTracker";

import ProtectedRoute from "./Components/ProtectedRoutes";
import SEO from "./Components/SEO";

// ✅ Admin pages are code-split — this JS is only downloaded when a
// visitor actually navigates to /admin, keeping the public bundle lean.
const AdminLogin = lazy(() => import("./Pages/Admin/AdminLogin"));
const Dashboard = lazy(() => import("./Pages/Admin/Dashboard"));
const ProductManagement = lazy(() => import("./Pages/Admin/ProductManagement"));
const QuotationManagement = lazy(() => import("./Pages/Admin/QuotationManagement"));
const CatalogueManagement = lazy(() => import("./Pages/Admin/CatalogueManagement"));
const Analytics = lazy(() => import("./Pages/Admin/Analytics"));
const ContactsManagement = lazy(() => import("./Pages/Admin/ContactsManagement"));
const BackupManagement = lazy(() => import("./Pages/Admin/BackupManagement"));

const withSeo = (page, seo) => (
  <>
    <SEO {...seo} />
    {page}
  </>
);

// ✅ Layout: hides NavBar & Footer on admin pages
const Layout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  return (
    <>
      {!isAdminRoute && <NavBar />}
      {children}
      {!isAdminRoute && <Footer />}
    </>
  );
};

// ✅ Simple fallback shown while an admin chunk is downloading
const AdminLoadingFallback = () => (
  <div className="loading">
    <div className="spinner"></div>
    <p>Loading...</p>
  </div>
);

function App() {
  return (
    <Router>
      <ScrollToTop />
      <AnalyticsTracker />
      <ReviewPrompt />
      <Layout>
        <Suspense fallback={<AdminLoadingFallback />}>
          <Routes>
            {/* Public Pages */}
            <Route
              path="/"
              element={withSeo(<Home />, {
                title: "Eco-Friendly Disposable Cutlery & Tableware Manufacturer",
                description:
                  "Krrish Ecoware Industries manufactures and trades eco-friendly disposable cutlery, paper plates, paper cups, wooden spoons, and food-service products from Delhi, India.",
                path: "/",
              })}
            />
            <Route
              path="/home"
              element={<Navigate to="/" replace />}
            />
            <Route path="/Home" element={<Navigate to="/" replace />} />
            <Route
              path="/about"
              element={withSeo(<About />, {
                title: "About Krrish Ecoware Industries",
                description:
                  "Learn about Krrish Ecoware Industries, a Delhi-based disposable products manufacturer and trader serving food-service businesses since 2001.",
                path: "/about",
              })}
            />
            <Route
              path="/products"
              element={withSeo(<Products />, {
                title: "Disposable Cutlery, Paper Plates & Food Packaging Products",
                description:
                  "Browse Krrish Ecoware Industries products including disposable cutlery, paper plates, wooden spoons, paper cups, foil containers, and food packaging supplies.",
                path: "/products",
              })}
            />
            <Route
              path="/products/catalogue"
              element={withSeo(<ProductCatalogue />, {
                title: "Product Catalogue",
                description:
                  "View and download the Krrish Impex product catalogue for disposable cutlery, paper plates, cups, food packaging, and tableware products.",
                path: "/products/catalogue",
              })}
            />
            <Route
              path="/certifications"
              element={withSeo(<Certifications />, {
                title: "Certifications & Quality Assurance",
                description:
                  "View Krrish Ecoware Industries certifications, registrations, and quality assurance credentials for reliable disposable tableware supply.",
                path: "/certifications",
              })}
            />
            <Route
              path="/infrastructure"
              element={withSeo(<Infrastructure />, {
                title: "Manufacturing Infrastructure & Production Capacity",
                description:
                  "See the manufacturing facility, production capacity, and operational infrastructure behind Krrish Ecoware Industries disposable product supply.",
                path: "/infrastructure",
              })}
            />
            <Route
              path="/contact"
              element={withSeo(<Contact />, {
                title: "Contact Krrish Ecoware Industries",
                description:
                  "Contact Krrish Ecoware Industries in Delhi for quotations, wholesale disposable cutlery, paper plates, tableware, and food packaging requirements.",
                path: "/contact",
              })}
            />

            {/* Admin Login */}
            <Route
              path="/admin"
              element={withSeo(<AdminLogin />, {
                title: "Admin Login",
                description: "Private administration area for Krrish Ecoware Industries.",
                path: "/admin",
                noIndex: true,
              })}
            />

            {/* ✅ Protected Admin Layout */}
            <Route
              path="/admin/dashboard"
              element={
                withSeo(
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>,
                  {
                    title: "Admin Dashboard",
                    description: "Private administration dashboard for Krrish Ecoware Industries.",
                    path: "/admin/dashboard",
                    noIndex: true,
                  }
                )
              }
            >
              {/* 👇 Nested routes */}
              <Route path="products" element={<ProductManagement />} />
              <Route path="catalogue" element={<CatalogueManagement />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="contacts" element={<ContactsManagement />} />
              <Route path="quotations" element={<QuotationManagement />} />
              <Route path="backup" element={<BackupManagement />} />

              {/* Optional default page */}
              <Route index element={<ProductManagement />} />
            </Route>
          </Routes>
        </Suspense>
      </Layout>
    </Router>
  );
}

export default App;